import { Box, useTheme } from '@mui/material';
import {
  TypographyElement,
  useContentElement,
  VisibilityScheme,
  useContentVisibility,
} from '@plugins/next-cms-core';
import PropTypes from 'prop-types';
import defaults from 'lodash/defaults';
import omitBy from 'lodash/omitBy';
import isNull from 'lodash/isNull';
import useConsent from '@lib/hooks/useConsent';
import { useCallback, useEffect, useRef } from 'react';
import ConsentRequiredFallback from '../molecules/ConsentRequiredFallback';

export default function FrameBlock(props) {
  const {
    data,
    throttleDelay = 200,
    readyStateDelay = 100,
  } = props;
  const embedRef = useRef(null);
  const lastExecutionTimeRef = useRef(0);
  const resizeObserverRef = useRef(null);
  const theme = useTheme();
  const hasConsent = useConsent('googleMaps');
  const { elementData } = useContentElement(
    data,
    FrameBlock.dataSchema,
  );

  const {
    elementId,
    isPerspectiveAutoHeightFrame,
  } = elementData;
  let title = null;
  const height = elementData.height ?? 400;
  const rootStyles = useContentVisibility(elementData.visibility, {
    paddingTop: { xs: theme.spacing(5), md: theme.spacing(10) },
    paddingBottom: { xs: theme.spacing(5), md: theme.spacing(10) },
  });

  // Main function that sends viewport height to the embedded content
  const sendViewportMessage = useCallback(() => {
    const embedElement = embedRef.current;

    // Check if embed element exists and has the necessary properties for postMessage
    if (embedElement
      && 'contentWindow' in embedElement
      && typeof embedElement.contentWindow === 'object'
      && 'postMessage' in embedElement.contentWindow
      && typeof embedElement.contentWindow.postMessage === 'function') {
      // Send viewport height message to the embedded content
      embedElement.contentWindow.postMessage({
        type: 'viewportHeight',
        viewportHeight: Math.min(embedElement.scrollHeight, window.innerHeight),
      }, '*');
    }
  }, []);

  // Throttled function to limit how often the main function executes
  const throttledViewportUpdate = useCallback((...args) => {
    const currentTime = Date.now();

    // Only execute if enough time has passed since last execution
    if (throttleDelay <= currentTime - lastExecutionTimeRef.current) {
      lastExecutionTimeRef.current = currentTime;
      sendViewportMessage();
    }
  }, [throttleDelay, sendViewportMessage]);

  // Handle messages from the embedded content
  const handleMessage = useCallback((messageEvent) => {
    const eventData = messageEvent?.data;
    const newHeight = eventData?.height;
    const embedElement = embedRef.current;

    // If message type is "embed-ready", trigger viewport update after delay
    if (eventData?.type === 'embed-ready') {
      setTimeout(throttledViewportUpdate, readyStateDelay);
    }

    // If height adjustment is enabled and we have a valid height value
    if (isPerspectiveAutoHeightFrame
      && embedElement
      && newHeight !== undefined) {
      // Set the embed element's height
      embedElement.style.height = `${newHeight}px`;
    }
  }, [isPerspectiveAutoHeightFrame, throttledViewportUpdate, readyStateDelay]);

  useEffect(() => {
    const embedElement = embedRef.current;

    if (!hasConsent || !embedElement) return () => {};

    // Set up ResizeObserver
    if (window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver(throttledViewportUpdate);
      resizeObserverRef.current.observe(embedElement);
    }

    // Add window resize listener
    window.addEventListener('resize', throttledViewportUpdate);

    // Add message listener
    window.addEventListener('message', handleMessage);

    // Cleanup function
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', throttledViewportUpdate);
      window.removeEventListener('message', handleMessage);
    };
  }, [throttledViewportUpdate, handleMessage, hasConsent]);

  if (!hasConsent) {
    return <ConsentRequiredFallback />;
  }

  if (elementData.title) {
    title = omitBy(elementData.title, isNull);
    defaults(title, {
      semanticVariant: 'h3',
      displayVariant: 'h3',
      textAlign: 'center',
    });
  }

  return (
    <Box sx={rootStyles}>
      {title && (
        <Box mb={5}>
          <TypographyElement data={title} />
        </Box>
      )}
      {hasConsent && (
        <iframe
          ref={embedRef}
          allowFullScreen="no"
          id={elementId}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          src={elementData.url}
          style={{
            border: 0,
            width: '100%',
            height: isPerspectiveAutoHeightFrame ? undefined : `${height}px`,
          }}
          title="Frame"
          width="100%"
        />
      )}
    </Box>
  );
}

FrameBlock.typeName = 'ComponentContentFrame'; // Strapi element type

FrameBlock.propTypes = {
  data: PropTypes.shape({
    title: TypographyElement.propTypes,
    url: PropTypes.string,
    height: PropTypes.number,
    visibility: VisibilityScheme.propTypes,
  }).isRequired,
};

FrameBlock.graphQlSchema = `
... on ${FrameBlock.typeName} {
  id
  elementId
  title {
    ${TypographyElement.graphQlSchema}
  }
  url
  height
  visibility {
    ${VisibilityScheme.graphQlSchema}
  }
  isPerspectiveAutoHeightFrame
}
`;
